/**
 * Unsaved Work Dialog
 * Asks before an action that would interrupt work still only in this tab
 * (stores/unsavedWorkStore): leaving a note, switching workspace. Same shape
 * as UnsavedChangesDialog; dismissal is the close X (house convention).
 */

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { MaterialIcon } from '../ui/MaterialIcon';
import { safeAnalytics } from '../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { describeUnsavedWork, labelsOf, useUnsavedWorkStore } from '../../stores/unsavedWorkStore';

interface UnsavedWorkDialogProps {
  isOpen: boolean;
  /** Which action asked; the analytics `surface` and the copy's verb. */
  surface: 'note_navigation' | 'workspace_switch';
  /** Keep going: the close X (house convention: no footer dismiss button). */
  onClose: () => void;
  onConfirm: () => void;
  isConfirming?: boolean;
}

const COPY = {
  note_navigation: { verb: 'Leaving this page', confirm: 'Leave anyway' },
  workspace_switch: { verb: 'Switching workspace', confirm: 'Switch anyway' },
} as const;

export const UnsavedWorkDialog: React.FC<UnsavedWorkDialogProps> = ({
  isOpen,
  surface,
  onClose,
  onConfirm,
  isConfirming = false,
}) => {
  const entries = useUnsavedWorkStore((s) => s.entries);
  // Leaving a page interrupts only that page's work; a switch interrupts all.
  const labels = labelsOf(entries, surface === 'note_navigation' ? 'page' : undefined);
  const { verb, confirm } = COPY[surface];
  // A recording is never lost to the interruption: both recorders save the
  // take on the way out (useVoiceRecording uploads it, Quick Capture keeps it
  // for "Capture restored"). Everything else in the register is.
  const recordingNote = labels.includes('a voice recording')
    ? ' The recording is saved before you go.'
    : '';

  const answer = (choice: 'keep' | 'proceed', action: () => void) => () => {
    safeAnalytics.track(ANALYTICS_EVENTS.UNSAVED_WORK_PROMPT_ANSWERED, {
      surface,
      choice,
      work: labels,
    });
    action();
  };
  const keep = answer('keep', onClose);
  const proceed = answer('proceed', onConfirm);

  return (
    <Modal
      isOpen={isOpen}
      onClose={keep}
      isCentered
      size={{ base: 'full', md: 'md' }}
      closeOnOverlayClick={!isConfirming}
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent mx={{ base: 0, md: 4 }}>
        <ModalHeader pb={2}>
          <VStack spacing={3} align="flex-start">
            <MaterialIcon name="pending" boxSize={8} color="text.warning" />
            <Text fontSize="lg" fontWeight="semibold" color="text.primary">
              Not saved yet
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton isDisabled={isConfirming} />

        <ModalBody py={4}>
          <Text fontSize="md" color="text.secondary" lineHeight="tall">
            {verb} now interrupts {describeUnsavedWork(labels)}.{recordingNote} Close this to keep
            going.
          </Text>
        </ModalBody>

        <ModalFooter gap={3} pt={2}>
          <Button onClick={proceed} isLoading={isConfirming} size="md">
            {confirm}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
